import { createContext, Dispatch, PropsWithChildren, useContext, useReducer } from "react";
import { RequestData } from "../types/request.types";

const RequestsContext = createContext<RequestsState>(null!);
const RequestsDispatchContext = createContext<Dispatch<RequestsAction>>(null!);

interface RequestsState {
  requests: RequestData[];
  totalPages: number;
  filter: string;
  sort: string | null;
  fetching: boolean;
  updating: boolean;
  filterOptions: {
    hideInactive: boolean;
    assignedToMe: boolean;
  };
}

const initialState: RequestsState = {
  requests: [],
  totalPages: 0,
  filter: 'all',
  sort: null,
  fetching: false,
  updating: false,
  filterOptions: {
    hideInactive: true,
    assignedToMe: false,
  },
};

type FetchRequestsAction = {
  type: 'FETCH_REQUESTS';
};

type SetRequestsAction = {
  type: 'SET_REQUESTS';
  requests: RequestData[];
  totalPages: number;
};

type SetSortAction = {
  type: 'SET_SORT';
  sort: string;
};

type SetFilterAction = {
  type: 'SET_FILTER';
  filter: string;
};

type SetHideInactive = {
  type: 'SET_HIDE_INACTIVE';
  hideInactive: boolean;
};

type SetAssignedToMe = {
  type: 'SET_ASSIGNED_TO_ME';
  assignedToMe: boolean;
};

type RequestsAction =
  | FetchRequestsAction
  | SetRequestsAction
  | SetSortAction
  | SetFilterAction
  | SetHideInactive
  | SetAssignedToMe;

export function RequestsProvider(
  { children }: PropsWithChildren<{}>
) {
  const [state, dispatch] = useReducer(requestsReducer, initialState);

  return (
    <RequestsContext.Provider value={state}>
      <RequestsDispatchContext.Provider value={dispatch}>
        {children}
      </RequestsDispatchContext.Provider>
    </RequestsContext.Provider>
  );
}

export function useRequests() {
  return useContext(RequestsContext);
}

export function useRequestsDispatch() {
  return useContext(RequestsDispatchContext);
}

function requestsReducer(
  state: RequestsState,
  action: RequestsAction
): RequestsState {
  switch (action.type) {
    case 'FETCH_REQUESTS':
      return { ...state, fetching: true, updating: true };
    case 'SET_REQUESTS':
      return {
        ...state,
        requests: action.requests,
        totalPages: action.totalPages,
        fetching: false,
        updating: false,
      };
    case 'SET_SORT':
      return { ...state, sort: action.sort, updating: true };
    case 'SET_FILTER':
      return { ...state, filter: action.filter, updating: true };
    case 'SET_HIDE_INACTIVE':
      return {
        ...state,
        filterOptions: { ...state.filterOptions, hideInactive: action.hideInactive },
        updating: true,
      };
    case 'SET_ASSIGNED_TO_ME':
      return {
        ...state,
        filterOptions: { ...state.filterOptions, assignedToMe: action.assignedToMe },
        updating: true,
      };
    default:
      return state;
  }
}